"use strict";
// The steps of the flow editor's toolbox: the one list.
//
// The flow editor draws its toolbox from this list (components/Tools/currentTools.ts in the frontend turns
// each entry into what the editor shows), and the AI tools (draft_flow, update_flow and
// list_flow_capabilities, through helpers/flow_catalogue.ts) read the same list. A step added here is in
// front of both, and nobody has to remember a second place.
//
// To the editor an entry means what it always meant: a label, a type, a service and an operation key, icons,
// help and sizes. Icons are written as names, not as images, because this file is also read outside the
// browser:
//   "./delay-dark.svg"                       a file in the frontend's src/assets/img/toolIcons folder
//   "/assets/img/operationTypeIcons/x.png"   a file in the frontend's public folder
// The editor turns either into the image it draws.
//
// To the AI tools an entry means what its optional `ai` block says:
//   - A step of another service (Stripe, HubSpot, Calendly and so on) needs no block. The AI tools offer it
//     as it is: its type is the label in lower case with underscores, after the service id unless the label
//     starts with it ("pipedrive_deal_won"), its name is the label, and its settings are read from its live
//     service definition when a flow is built. Give it `ai.id` to keep that type if the label is ever
//     changed, and `ai.name` or `ai.about` to word it for the AI.
//   - A step of Encharge itself needs a hand-written entry in flow_catalogue.ts, which says how its settings
//     are written. Until there is one, flow_catalogue.spec.ts fails. A step the AI tools should not build is
//     marked `ai.notForAi`, with the reason.
//   - Hidden steps and notes are not steps the toolbox offers. Both are left out.
//
// Keep this file free of imports from the app (stores, images, React): the frontend bundles it and the
// server loads it.
Object.defineProperty(exports, "__esModule", { value: true });
exports.isToolboxStep = exports.toolboxKey = exports.TOOLBOX_STEPS = void 0;
/** The toolbox shows Trial Ends under each billing app. They are one step, trial_ends, and share one reason. */
const TRIAL_ENDS_COPY = {
    name: "Trial Ends for Stripe, Chargebee, Paddle, Recurly and Chargify",
    why: "They are the same step as trial_ends, shown under each billing integration in the toolbox, with the same settings and the same behaviour. Use trial_ends.",
};
exports.TOOLBOX_STEPS = [
    // TRIGGERS SECTION
    {
        display: {
            label: "Time Trigger",
            operationIcon: "./delay-dark.svg",
            operationIconToolbox: "./delay-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/time",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601804e86867724dfc6ee0ff",
            markdown: `The __Time Trigger__ allows you to start a flow on a specific time.

On that time, people who are in the selected segment will proceed to the next steps.

For example, you can use it to send an email to your subscribers on Monday morning at 10 AM. 

***
*Notes:*

- *People can pass through a __Time Trigger__ only once. If you want to change the date of a __Time Trigger__ that has already run, it's recommended to create a new __Time Trigger__.*

- *The selected time can not be in the past.*
`,
        },
    },
    {
        display: {
            label: "Recurring Time Trigger",
            operationIcon: "./delay-dark.svg",
            operationIconToolbox: "./delay-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/recurring-time-trigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "642ed0241b7ba105b02ef7b7",
        },
    },
    {
        display: {
            label: "Entered Segment",
            operationIcon: "./segment-dark.svg",
            operationIconToolbox: "./segment-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/entered-segment",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "60180b7f6867724dfc6ee143",
        },
    },
    {
        display: {
            label: "Left Segment",
            operationIcon: "./segment-dark.svg",
            operationIconToolbox: "./segment-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/left-segment",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "60180e96a4cefb30ae5c5e8c",
        },
    },
    {
        display: {
            label: "Email Activity",
            operationIcon: "./email-aux-dark.svg",
            operationIconToolbox: "./email-aux-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/email-trigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6018103c1f25b9041beb9f5a",
        },
        isHidden: true,
    },
    {
        display: {
            label: "Email Activity",
            operationIcon: "./email-aux-dark.svg",
            operationIconToolbox: "./email-aux-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/email-trigger-v2",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6018103c1f25b9041beb9f5a",
        },
    },
    {
        display: {
            label: "Tag Added",
            operationIcon: "./tag-dark.svg",
            operationIconToolbox: "./tag-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/added-tag",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6018288ea4cefb30ae5c5f9e",
        },
    },
    {
        display: {
            label: "Tag Removed",
            operationIcon: "./tag-dark.svg",
            operationIconToolbox: "./tag-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/removed-tag",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "601901406867724dfc6ee67f",
        },
    },
    {
        display: {
            label: "Field Changed",
            operationIcon: "./change-field-dark.svg",
            operationIconToolbox: "./change-field-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/change-field-trigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "65084f57790a583e6b3b3baf",
        },
    },
    {
        display: {
            label: "Event",
            operationIcon: "./event-dark.svg",
            operationIconToolbox: "./event-light.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "encharge",
        operationKey: "/event",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6019027cac2f834ec5384299",
        },
    },
    {
        display: {
            label: "Segment.com Event",
            operationIcon: "/assets/img/operationTypeIcons/segment-logo-dark.svg",
            operationIconToolbox: "/assets/img/operationTypeIcons/segment-logo-light.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "segment",
        operationKey: "/event",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "601911192042ff6d1b2a98d1",
        },
    },
    {
        display: {
            label: "Date Field Trigger",
            operationIcon: "./date-field-trigger.svg",
            operationIconToolbox: "./date-field-trigger-toolbox.svg",
        },
        serviceId: "encharge",
        operationKey: "/dateFieldTrigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6131ef8012c07c18afdd0b5e",
        },
    },
    {
        display: {
            label: "Birthday/Annual Trigger",
            operationIcon: "./date-field-trigger.svg",
            operationIconToolbox: "./date-field-trigger-toolbox.svg",
        },
        serviceId: "encharge",
        operationKey: "/annualTrigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "613226c76c65aa15b87d7054",
        },
    },
    {
        display: {
            label: "Receive Webhook",
            operationIcon: "./webhook-dark.svg",
            operationIconToolbox: "./webhook-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/webhook-trigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "60191718ac2f834ec53842f4",
        },
    },
    {
        display: {
            label: "Encharge Form Submitted",
            operationIcon: "./submitted-form-dark.svg",
            operationIconToolbox: "./submitted-form-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/native-form-submitted",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6401bd6b85990362ed9afbe1",
        },
    },
    {
        display: {
            label: "Tracked Form Submitted",
            operationIcon: "./submitted-form-dark.svg",
            operationIconToolbox: "./submitted-form-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/site-form-submitted",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "611df581f886c9486f8da4b7",
        },
    },
    {
        display: {
            label: "External Form Submitted",
            operationIcon: "./submitted-form-dark.svg",
            operationIconToolbox: "./submitted-form-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/submitted-form",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "601923256867724dfc6ee732",
        },
    },
    {
        display: {
            label: "Row Added in Google Sheets",
            operationIcon: "./google-sheets.svg",
            operationIconToolbox: "./google-sheets-toolbox.svg",
        },
        serviceId: "googlesheets",
        operationKey: "/sheets/row-added",
        type: "trigger",
        ai: { id: "googlesheets_row_added" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "66f24480c263c66781cab012",
        },
    },
    {
        display: {
            label: "Facebook Lead Ad Submitted",
            operationIcon: "./facebook-dark.svg",
            operationIconToolbox: "./facebook-light.svg",
        },
        serviceId: "facebook",
        operationKey: "/lead-forms/leads",
        type: "trigger",
        ai: { id: "facebook_lead_ad_submitted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "671657299901d46da1e05509",
        },
    },
    {
        display: {
            label: "Typeform Submitted",
            operationIcon: "./typeform-dark.svg",
            operationIconToolbox: "./typeform-light.svg",
        },
        serviceId: "typeform",
        operationKey: "/entries",
        type: "trigger",
        ai: { id: "typeform_submitted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "601935331f25b9041beba5c5",
        },
    },
    {
        display: {
            label: "Page Visited",
            operationIcon: "./page-visited.svg",
            operationIconToolbox: "./page-visited-toolbox.svg",
        },
        serviceId: "encharge",
        operationKey: "/page-visited-trigger",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "60195f9bac2f834ec53844fb",
        },
    },
    {
        display: {
            label: "New Mailchimp Subscriber",
            operationIcon: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
        },
        serviceId: "mailchimp",
        operationKey: "/subscribers",
        type: "trigger",
        ai: { id: "mailchimp_new_subscriber" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "601a5b681f25b9041bebada7",
        },
    },
    {
        display: {
            label: "New Mailchimp Unsubscriber",
            operationIcon: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
        },
        serviceId: "mailchimp",
        operationKey: "/unsubscribers",
        type: "trigger",
        ai: { id: "mailchimp_new_unsubscriber" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "601a9e0dac2f834ec5384cfe",
        },
    },
    {
        display: {
            label: "New Intercom Lead",
            operationIcon: "./intercom.svg",
            operationIconToolbox: "./intercom-toolbox.svg",
        },
        serviceId: "intercom",
        operationKey: "/leads",
        type: "trigger",
        ai: { id: "intercom_new_lead" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601aa5d61f25b9041bebaf45",
        },
    },
    {
        display: {
            label: "New Intercom User",
            operationIcon: "./intercom.svg",
            operationIconToolbox: "./intercom-toolbox.svg",
        },
        serviceId: "intercom",
        operationKey: "/users",
        type: "trigger",
        ai: { id: "intercom_new_user" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601aaec92042ff6d1b2aa35a",
        },
    },
    {
        display: {
            label: "Tagged in Intercom",
            operationIcon: "./intercom.svg",
            operationIconToolbox: "./intercom-toolbox.svg",
        },
        serviceId: "intercom",
        operationKey: "/added-tag",
        type: "trigger",
        ai: { id: "intercom_tagged" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab14b1f25b9041bebaf98",
        },
    },
    {
        display: {
            label: "New Stripe Payment",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/payments",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
        isHidden: true,
    },
    {
        display: {
            label: "New WooCommerce Order",
            operationIcon: "./woocommerce-dark.svg",
            operationIconToolbox: "./woocommerce-light.svg",
        },
        serviceId: "woocommerce",
        operationKey: "/orders",
        type: "trigger",
        ai: { id: "woocommerce_new_order" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New WooCommerce Customer",
            operationIcon: "./woocommerce-dark.svg",
            operationIconToolbox: "./woocommerce-light.svg",
        },
        serviceId: "woocommerce",
        operationKey: "/customers",
        type: "trigger",
        ai: { id: "woocommerce_new_customer" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "Trial Started",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/trialStarted",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
        isHidden: true,
    },
    {
        display: {
            label: "Canceled Subscription",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionCanceled",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
        isHidden: true,
    },
    {
        display: {
            label: "Past Due Subscription",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionPastDue",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
        isHidden: true,
    },
    {
        display: {
            label: "Activated Subscription",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionActive",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
        isHidden: true,
    },
    {
        display: {
            label: "All Stripe Events",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Trial Started",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/trialStarted-v2",
        type: "trigger",
        ai: { id: "stripe_trial_started" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Trial Ends",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "encharge",
        serviceIdForFiltering: "stripe",
        operationKey: "/trialEndsStripe",
        type: "trigger",
        ai: { notForAi: TRIAL_ENDS_COPY },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Trial Converted",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/trialConverted-v2",
        type: "trigger",
        ai: { id: "stripe_trial_converted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Trial Expired",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/trialExpired-v2",
        type: "trigger",
        ai: { id: "stripe_trial_expired" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Activated",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionActive-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_activated" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Past Due",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionPastDue-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_past_due" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Reactivated",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionReactivated-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_reactivated" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Scheduled Subscription Cancellation",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/scheduledCancellation-v2",
        type: "trigger",
        ai: { id: "stripe_scheduled_subscription_cancellation" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Canceled",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionCanceled-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_canceled" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Expanded",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionExpanded-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_expanded" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "Subscription Contracted",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/subscriptionContracted-v2",
        type: "trigger",
        ai: { id: "stripe_subscription_contracted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            // configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "New Stripe Payment",
            operationIcon: "./stripe-dark.svg",
            operationIconToolbox: "./stripe-light.svg",
        },
        serviceId: "stripe",
        operationKey: "/payments-v2",
        type: "trigger",
        ai: { id: "stripe_new_payment" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601ab7e2ac2f834ec5384dc9",
        },
    },
    {
        display: {
            label: "New HubSpot Contact",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/contacts",
        type: "trigger",
        ai: { id: "hubspot_new_contact" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbd341f25b9041bebb5b0",
        },
    },
    {
        display: {
            label: "HubSpot Contact Field Match",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/contacts-updated",
        type: "trigger",
        ai: { id: "hubspot_contact_field_match" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bc0d31f25b9041bebb5c2",
        },
    },
    {
        display: {
            label: "HubSpot Deal Stage Match",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/deals/stage-match",
        type: "trigger",
        ai: { id: "hubspot_deal_stage_match" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bc449ac2f834ec53853ae",
        },
    },
    {
        display: {
            label: "Added to List in HubSpot",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/lists/added",
        type: "trigger",
        ai: { id: "hubspot_added_to_list" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7a318a552811521e5955",
        },
    },
    {
        display: {
            label: "HubSpot Form Submitted",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/forms/submissions",
        type: "trigger",
        ai: { id: "hubspot_form_submitted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7b2f8a552811521e5957",
        },
    },
    {
        display: {
            label: "Meeting Booked in HubSpot",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/meetings/booked",
        type: "trigger",
        ai: { id: "hubspot_meeting_booked" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7b434d805871ceaa3c38",
        },
    },
    {
        display: {
            label: "New Salesforce Lead",
            operationIcon: "./salesforce-dark.svg",
            operationIconToolbox: "./salesforce-light.svg",
        },
        serviceId: "salesforce",
        operationKey: "/new-leads",
        type: "trigger",
        ai: { id: "salesforce_new_lead" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bd8f3fb34b55df443d39f",
        },
    },
    {
        display: {
            label: "New Salesforce Contact",
            operationIcon: "./salesforce-dark.svg",
            operationIconToolbox: "./salesforce-light.svg",
        },
        serviceId: "salesforce",
        operationKey: "/new-contacts",
        type: "trigger",
        ai: { id: "salesforce_new_contact" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bdba01f25b9041bebb665",
        },
    },
    {
        display: {
            label: "Salesforce Field Match",
            operationIcon: "./salesforce-dark.svg",
            operationIconToolbox: "./salesforce-light.svg",
        },
        serviceId: "salesforce",
        operationKey: "/field-match",
        type: "trigger",
        ai: { id: "salesforce_field_match" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bc7f21f25b9041bebb5ec",
        },
    },
    // Chargebee
    {
        display: {
            label: "All Chargebee Events",
            operationIcon: "./chargebee-logo.svg",
            operationIconToolbox: "./chargebee-logo-toolbox.svg",
        },
        serviceId: "chargebee",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Chargebee Trial Ends",
            operationIcon: "./chargebee-logo.svg",
            operationIconToolbox: "./chargebee-logo-toolbox.svg",
        },
        serviceId: "encharge",
        serviceIdForFiltering: "chargebee",
        operationKey: "/trialEndsChargebee",
        type: "trigger",
        ai: { notForAi: TRIAL_ENDS_COPY },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb48fac2f834ec5385361",
        },
    },
    {
        display: {
            label: "Chargebee Trial Started",
            operationIcon: "./chargebee-logo.svg",
            operationIconToolbox: "./chargebee-logo-toolbox.svg",
        },
        serviceId: "chargebee",
        operationKey: "/trialStarted",
        type: "trigger",
        ai: { id: "chargebee_trial_started" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb48fac2f834ec5385361",
        },
    },
    {
        display: {
            label: "Activated Subscription",
            operationIcon: "./chargebee-logo.svg",
            operationIconToolbox: "./chargebee-logo-toolbox.svg",
        },
        serviceId: "chargebee",
        operationKey: "/subscriptionActivated",
        type: "trigger",
        ai: { id: "chargebee_activated_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb48fac2f834ec5385361",
        },
    },
    {
        display: {
            label: "Canceled Subscription",
            operationIcon: "./chargebee-logo.svg",
            operationIconToolbox: "./chargebee-logo-toolbox.svg",
        },
        serviceId: "chargebee",
        operationKey: "/subscriptionCanceled",
        type: "trigger",
        ai: { id: "chargebee_canceled_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb48fac2f834ec5385361",
        },
    },
    // Paddle
    {
        display: {
            label: "All Paddle Events",
            operationIcon: "/assets/img/operationTypeIcons/paddle-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/paddle-logo-toolbox.png",
        },
        serviceId: "paddle",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Trial Ends",
            operationIcon: "/assets/img/operationTypeIcons/paddle-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/paddle-logo-toolbox.png",
        },
        serviceId: "encharge",
        serviceIdForFiltering: "paddle",
        operationKey: "/trialEndsPaddle",
        type: "trigger",
        ai: { notForAi: TRIAL_ENDS_COPY },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb9226867724dfc6ef76a",
        },
    },
    {
        display: {
            label: "Trial Started",
            operationIcon: "/assets/img/operationTypeIcons/paddle-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/paddle-logo-toolbox.png",
        },
        serviceId: "paddle",
        operationKey: "/trialStarted",
        type: "trigger",
        ai: { id: "paddle_trial_started" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb9226867724dfc6ef76a",
        },
    },
    {
        display: {
            label: "Activated Subscription",
            operationIcon: "/assets/img/operationTypeIcons/paddle-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/paddle-logo-toolbox.png",
        },
        serviceId: "paddle",
        operationKey: "/subscriptionActivated",
        type: "trigger",
        ai: { id: "paddle_activated_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb9226867724dfc6ef76a",
        },
    },
    {
        display: {
            label: "Canceled Subscription",
            operationIcon: "/assets/img/operationTypeIcons/paddle-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/paddle-logo-toolbox.png",
        },
        serviceId: "paddle",
        operationKey: "/subscriptionCanceled",
        type: "trigger",
        ai: { id: "paddle_canceled_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bb9226867724dfc6ef76a",
        },
    },
    // Recurly
    {
        display: {
            label: "All Recurly Events",
            operationIcon: "./recurly-logo.svg",
            operationIconToolbox: "./recurly-logo-toolbox.svg",
        },
        serviceId: "recurly",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Recurly Trial Ends",
            operationIcon: "./recurly-logo.svg",
            operationIconToolbox: "./recurly-logo-toolbox.svg",
        },
        serviceId: "encharge",
        serviceIdForFiltering: "recurly",
        operationKey: "/trialEndsRecurly",
        type: "trigger",
        ai: { notForAi: TRIAL_ENDS_COPY },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbbf2fb34b55df443d2ee",
        },
    },
    {
        display: {
            label: "Recurly Trial Started",
            operationIcon: "./recurly-logo.svg",
            operationIconToolbox: "./recurly-logo-toolbox.svg",
        },
        serviceId: "recurly",
        operationKey: "/trialStarted",
        type: "trigger",
        ai: { id: "recurly_trial_started" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbbf2fb34b55df443d2ee",
        },
    },
    {
        display: {
            label: "Activated Subscription",
            operationIcon: "./recurly-logo.svg",
            operationIconToolbox: "./recurly-logo-toolbox.svg",
        },
        serviceId: "recurly",
        operationKey: "/subscriptionActivated",
        type: "trigger",
        ai: { id: "recurly_activated_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbbf2fb34b55df443d2ee",
        },
    },
    {
        display: {
            label: "Canceled Subscription",
            operationIcon: "./recurly-logo.svg",
            operationIconToolbox: "./recurly-logo-toolbox.svg",
        },
        serviceId: "recurly",
        operationKey: "/subscriptionCanceled",
        type: "trigger",
        ai: { id: "recurly_canceled_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbbf2fb34b55df443d2ee",
        },
    },
    // Chargify
    {
        display: {
            label: "All Chargify Events",
            operationIcon: "/assets/img/operationTypeIcons/chargify-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/chargify-logo-toolbox.png",
        },
        serviceId: "chargify",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Chargify Trial Ends",
            operationIcon: "/assets/img/operationTypeIcons/chargify-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/chargify-logo-toolbox.png",
        },
        serviceId: "encharge",
        serviceIdForFiltering: "chargify",
        operationKey: "/trialEndsChargify",
        type: "trigger",
        ai: { notForAi: TRIAL_ENDS_COPY },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbc331f25b9041bebb5a9",
        },
    },
    {
        display: {
            label: "Chargify Trial Started",
            operationIcon: "/assets/img/operationTypeIcons/chargify-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/chargify-logo-toolbox.png",
        },
        serviceId: "chargify",
        operationKey: "/trialStarted",
        type: "trigger",
        ai: { id: "chargify_trial_started" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbc331f25b9041bebb5a9",
        },
    },
    {
        display: {
            label: "Activated Subscription",
            operationIcon: "/assets/img/operationTypeIcons/chargify-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/chargify-logo-toolbox.png",
        },
        serviceId: "chargify",
        operationKey: "/subscriptionActivated",
        type: "trigger",
        ai: { id: "chargify_activated_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbc331f25b9041bebb5a9",
        },
    },
    {
        display: {
            label: "Canceled Subscription",
            operationIcon: "/assets/img/operationTypeIcons/chargify-logo.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/chargify-logo-toolbox.png",
        },
        serviceId: "chargify",
        operationKey: "/subscriptionCanceled",
        type: "trigger",
        ai: { id: "chargify_canceled_subscription" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bbc331f25b9041bebb5a9",
        },
    },
    // Calendly
    {
        display: {
            label: "All Calendly Events",
            operationIcon: "./calendly-logo.svg",
            operationIconToolbox: "./calendly-logo-toolbox.svg",
        },
        serviceId: "calendly",
        operationKey: "/allEvents",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        isHidden: true,
    },
    {
        display: {
            label: "Booked Calendly Meeting",
            operationIcon: "./calendly-logo.svg",
            operationIconToolbox: "./calendly-logo-toolbox.svg",
        },
        serviceId: "calendly",
        operationKey: "/bookedMeeting",
        type: "trigger",
        ai: { id: "calendly_booked_meeting" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601bebf4ac2f834ec5385480",
        },
    },
    {
        display: {
            label: "Canceled Calendly Meeting",
            operationIcon: "./calendly-logo.svg",
            operationIconToolbox: "./calendly-logo-toolbox.svg",
        },
        serviceId: "calendly",
        operationKey: "/canceledMeeting",
        type: "trigger",
        ai: { id: "calendly_canceled_meeting" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "601beff6a4cefb30ae5c7586",
        },
    },
    {
        display: {
            label: "Trial Ends",
            operationIcon: "./trial-ends.svg",
            operationIconToolbox: "./trial-ends-toolbox.svg",
        },
        serviceId: "encharge",
        operationKey: "/trialEnds",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "602551f88502d1120e90684e",
        },
    },
    // END OF TRIGGERS SECTION
    // ACTIONS
    {
        display: {
            label: "Send Email",
            operationIcon: "./email-dark.svg",
            operationIconToolbox: "./email-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/send",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "601c0380ac2f834ec53854fa",
        },
    },
    {
        display: {
            label: "Wait",
            operationIcon: "./wait-dark.svg",
            operationIconToolbox: "./wait-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/delay",
        type: "action",
        inputFields: {},
        mustConfig: {
            configStep: true,
        },
        outputFields: {},
        isHidden: true,
    },
    {
        display: {
            label: "Wait",
            operationIcon: "./wait-dark.svg",
            operationIconToolbox: "./wait-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/delay-v2",
        type: "action",
        inputFields: {},
        mustConfig: {
            configStep: false,
        },
        outputFields: {},
        helpDocs: {
            helpScoutID: "601d052cac2f834ec5385b58",
        },
    },
    {
        display: {
            label: "Change Field",
            operationIcon: "./change-field-dark.svg",
            operationIconToolbox: "./change-field-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/change-field",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "602132866867724dfc6f0ac0",
        },
    },
    {
        display: {
            label: "Change Numeric Field",
            operationIcon: "./change-numeric-field-dark.svg",
            operationIconToolbox: "./change-numeric-field-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/change-numeric-field",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "63bea9ac523e535f3403a456",
        },
    },
    {
        display: {
            label: "Add Tag",
            operationIcon: "./tag-dark.svg",
            operationIconToolbox: "./tag-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/add-tag",
        type: "action",
        inputFields: {},
        mustConfig: {
            configStep: true,
        },
        outputFields: {},
        helpDocs: {
            helpScoutID: "601d1027ac2f834ec5385b85",
        },
    },
    {
        display: {
            label: "Remove Tag",
            operationIcon: "./tag-dark.svg",
            operationIconToolbox: "./tag-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/remove-tag",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "601d13931f25b9041bebbd70",
        },
    },
    {
        display: {
            label: "Goal",
            operationIcon: "./flow-goal.svg",
            operationIconToolbox: "./flow-goal.svg",
        },
        serviceId: "encharge",
        operationKey: "/flow-goal",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "End Flow",
            operationIcon: "./end-flow.svg",
            operationIconToolbox: "./end-flow-tool.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/end-flow",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {},
        isHidden: true,
    },
    {
        display: {
            label: "End Flow",
            operationIcon: "./end-flow.svg",
            operationIconToolbox: "./end-flow-tool.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/end-flow-v2",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {},
        helpDocs: {
            helpScoutID: "6024ea49a4cefb30ae5c9d0b",
        },
    },
    {
        display: {
            label: "Lead Score",
            operationIcon: "./lead-score.svg",
            operationIconToolbox: "./lead-score-toolbox.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/lead-score",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023ee33fb34b55df443f63e",
        },
    },
    {
        display: {
            label: "A/B Test",
            operationIcon: "./ab-test-dark.svg",
            operationIconToolbox: "./ab-test-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/ab-test",
        type: "action",
        inputFields: {},
        mustConfig: {
            configStep: false,
        },
        outputFields: {},
        isHidden: false,
        helpDocs: {
            helpScoutID: "602241766867724dfc6f106a",
        },
    },
    {
        display: {
            label: "Send Email Notification",
            operationIcon: "./email-notification.svg",
            operationIconToolbox: "./email-notification-toolbox.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "encharge",
        operationKey: "/email-notification",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "60214ae1a4cefb30ae5c86f7",
        },
    },
    {
        display: {
            label: "Send Slack Notification",
            operationIcon: "./slack.svg",
            operationIconToolbox: "./slack-toolbox.svg",
            operationIconApps: "./slack-fullcolor.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "slack",
        operationKey: "/post-message",
        type: "action",
        ai: { id: "slack_send_notification" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "602258dcac2f834ec5386c7f",
        },
    },
    {
        display: {
            label: "Add a Row in Google Sheets",
            operationIcon: "./google-sheets.svg",
            operationIconToolbox: "./google-sheets-toolbox.svg",
        },
        serviceId: "googlesheets",
        operationKey: "/sheets/rows",
        type: "action",
        ai: { id: "googlesheets_add_a_row" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "66f2448ef6180530271e2664",
        },
    },
    {
        display: {
            label: "Send Webhook",
            operationIcon: "./webhook-dark.svg",
            operationIconToolbox: "./webhook-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/send-webhook",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mayMapOutputFields: true,
        },
        helpDocs: {
            helpScoutID: "60228e6da4cefb30ae5c8df5",
        },
    },
    {
        display: {
            label: "Send SMS",
            operationIcon: "./twilio.svg",
            operationIconToolbox: "./twilio-toolbox.svg",
        },
        serviceId: "twilio",
        operationKey: "/send-sms",
        type: "action",
        ai: { id: "twilio_send_sms" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "60b4e51cafcffb241935fd15",
        },
    },
    {
        display: {
            label: "Send In-App Message",
            operationIcon: "./intercom.svg",
            operationIconToolbox: "./intercom-toolbox.svg",
        },
        serviceId: "intercom",
        operationKey: "/messages",
        type: "action",
        ai: { id: "intercom_send_in_app_message" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6022a16fac2f834ec5386ead",
        },
    },
    {
        display: {
            label: "Add Intercom Tag",
            operationIcon: "./intercom.svg",
            operationIconToolbox: "./intercom-toolbox.svg",
        },
        serviceId: "intercom",
        operationKey: "/add-tag",
        type: "action",
        ai: { id: "intercom_add_tag" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6022b1afa4cefb30ae5c8f5f",
        },
    },
    {
        display: {
            label: "Add to Audience",
            operationIcon: "./facebook-dark.svg",
            operationIconToolbox: "./facebook-light.svg",
        },
        serviceId: "facebook",
        operationKey: "/add-to-audience",
        type: "action",
        ai: { id: "facebook_add_to_audience" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "602399d86867724dfc6f1a34",
        },
    },
    {
        display: {
            label: "Remove from Audience",
            operationIcon: "./facebook-dark.svg",
            operationIconToolbox: "./facebook-light.svg",
        },
        serviceId: "facebook",
        operationKey: "/remove-from-audience",
        type: "action",
        ai: { id: "facebook_remove_from_audience" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
        },
        helpDocs: {
            helpScoutID: "6023bbf41f25b9041bebd84c",
        },
    },
    {
        display: {
            label: "Subscribe to Mailchimp",
            operationIcon: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
        },
        serviceId: "mailchimp",
        operationKey: "/add-subscriber",
        type: "action",
        ai: { id: "mailchimp_subscribe" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6022b4dda4cefb30ae5c8f82",
        },
    },
    {
        display: {
            label: "Unsubscribe from Mailchimp",
            operationIcon: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
        },
        serviceId: "mailchimp",
        operationKey: "/unsubscribe-subscriber",
        type: "action",
        ai: { id: "mailchimp_unsubscribe" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023913d6867724dfc6f1a05",
        },
    },
    {
        display: {
            label: "Remove from Mailchimp",
            operationIcon: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
            operationIconToolbox: "/assets/img/operationTypeIcons/mailchimp-logo-dark.png",
        },
        serviceId: "mailchimp",
        operationKey: "/remove-subscriber",
        type: "action",
        ai: { id: "mailchimp_remove" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023964da4cefb30ae5c9587",
        },
    },
    {
        display: {
            label: "Create HubSpot Record",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "hubspot",
        operationKey: "/objects/create",
        type: "action",
        ai: { id: "hubspot_create_record" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7bac8a552811521e5959",
        },
    },
    {
        display: {
            label: "Update HubSpot Record",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
            drawerSize: {
                setup: "lg",
            },
        },
        serviceId: "hubspot",
        operationKey: "/objects/update",
        type: "action",
        ai: { id: "hubspot_update_record" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7bb99471985a5ac52b0f",
        },
    },
    {
        display: {
            label: "Create HubSpot Deal",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/deals",
        type: "action",
        ai: { id: "hubspot_create_deal" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023d2fc1f25b9041bebd89d",
        },
    },
    {
        display: {
            label: "Change HubSpot Deal Stage",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/deals/change-stage",
        type: "action",
        ai: { id: "hubspot_change_deal_stage" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023dc52ac2f834ec53876fa",
        },
    },
    {
        display: {
            label: "Create Task in HubSpot",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/create-task",
        type: "action",
        ai: { id: "hubspot_create_task" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023e205a4cefb30ae5c973d",
        },
    },
    {
        display: {
            label: "Add to List in HubSpot",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/lists/add",
        type: "action",
        ai: { id: "hubspot_add_to_list" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023e205a4cefb30ae5c973d",
        },
    },
    {
        display: {
            label: "Remove from List in HubSpot",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/lists/remove",
        type: "action",
        ai: { id: "hubspot_remove_from_list" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "634c7bd1de258f5018eb4c42",
        },
    },
    {
        display: {
            label: "Create Lead/Contact",
            operationIcon: "./salesforce-dark.svg",
            operationIconToolbox: "./salesforce-light.svg",
        },
        serviceId: "salesforce",
        operationKey: "/create-lead-contact",
        type: "action",
        ai: { id: "salesforce_create_lead_contact" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6023e900ac2f834ec538774e",
        },
    },
    {
        display: {
            label: "Archive Person",
            operationIcon: "./archive.svg",
            operationIconToolbox: "./archive-tool.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/archive",
        type: "action",
        inputFields: {},
        outputFields: {},
        mustConfig: {},
        helpDocs: {
            helpScoutID: "6024f8cfac2f834ec5387d14",
        },
    },
    // END OF ACTIONS SECTION
    // FILTERS ACTIONS SECTION
    {
        display: {
            label: "Check Email Activity",
            operationIcon: "./email-aux-dark.svg",
            operationIconToolbox: "./email-aux-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/email-activity",
        type: "filter",
        inputFields: {},
        outputFields: {},
        mustConfig: {},
        helpDocs: {
            helpScoutID: "602504d424d2d21e45ed5561",
        },
    },
    {
        display: {
            label: "Check Condition",
            operationIcon: "./change-field-dark.svg",
            operationIconToolbox: "./change-field-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/check-field",
        type: "filter",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "602531d8661b720174a6c275",
        },
    },
    {
        display: {
            label: "Is in Segment",
            operationIcon: "./segment-dark.svg",
            operationIconToolbox: "./segment-light.svg",
        },
        serviceId: "encharge",
        operationKey: "/segment-filter",
        type: "filter",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "60253e470a2dae5b58faeb9f",
        },
    },
    {
        display: {
            label: "Has Visited Page",
            operationIcon: "./page-visited.svg",
            operationIconToolbox: "./page-visited-toolbox.svg",
        },
        serviceId: "encharge",
        operationKey: "/page-visited-filter",
        type: "filter",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "6025401d8502d1120e9067ab",
        },
    },
    {
        display: {
            label: "Has Tag",
            operationIcon: "./tag-dark.svg",
            operationIconToolbox: "./tag-light.svg",
            size: "small",
        },
        serviceId: "encharge",
        operationKey: "/has-tag",
        type: "filter",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "602543560a2dae5b58faebcb",
        },
    },
    {
        display: {
            label: "Has Booked Meeting",
            operationIcon: "./calendly-logo.svg",
            operationIconToolbox: "./calendly-logo-toolbox.svg",
        },
        serviceId: "calendly",
        operationKey: "/hasBookedMeeting",
        type: "filter",
        ai: { id: "calendly_has_booked_meeting" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "60254538b3ebfb109b5815a1",
        },
    },
    {
        display: {
            label: "Check HubSpot Record",
            operationIcon: "./hubspot.svg",
            operationIconToolbox: "./hubspot-toolbox.svg",
        },
        serviceId: "hubspot",
        operationKey: "/objects/check",
        type: "filter",
        ai: { id: "hubspot_check_record" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
        helpDocs: {
            helpScoutID: "63738935190e8f786b4446bf",
        },
    },
    // END OF FILTERS SECTION
    // DECORATIONS SECTION
    {
        display: {
            label: "Note",
            operationIcon: "./note.svg",
            operationIconToolbox: "./note.svg",
        },
        serviceId: "encharge",
        operationKey: "/note",
        type: "decoration",
        inputFields: {},
        outputFields: {},
        // helpDocs: {
        //   helpScoutID: "60254a0d24d2d21e45ed557b",
        // },
    },
    {
        display: {
            label: "Annotation",
            operationIcon: "./annotation.svg",
            operationIconToolbox: "./annotation.svg",
        },
        serviceId: "encharge",
        operationKey: "/annotation",
        type: "decoration",
        inputFields: {},
        outputFields: {},
        // helpDocs: {
        //   helpScoutID: "60254a0d24d2d21e45ed557b",
        // },
    },
    // {
    //   display: {
    //     label: "Contacts from Google Sheets",
    //     operationIcon:
    //       "https://s3.amazonaws.com/static.encharge.io/common/img/logo-googlesheets.png"
    //   },
    //   serviceId: "typeform",
    //   operationKey: "triggers.endUserFromCreatedRow",
    //   type: "trigger",
    //   inputFields: {},
    //   outputFields: {}
    // }
    {
        display: {
            label: "New WooCommerce Customer",
            operationIcon: "./woocommerce-dark.svg",
            operationIconToolbox: "./woocommerce-light.svg",
        },
        serviceId: "woocommerce",
        operationKey: "/customers",
        type: "trigger",
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New Shopify Customer",
            operationIcon: "./shopify.svg",
            operationIconToolbox: "./shopify.svg",
        },
        serviceId: "shopify",
        operationKey: "/customers",
        type: "trigger",
        ai: { id: "shopify_new_customer" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New Shopify Order",
            operationIcon: "./shopify.svg",
            operationIconToolbox: "./shopify.svg",
        },
        serviceId: "shopify",
        operationKey: "/orders",
        type: "trigger",
        ai: { id: "shopify_new_order" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New ThriveCart Order",
            operationIcon: "./thrivecart.svg",
            operationIconToolbox: "./thrivecart.svg",
        },
        serviceId: "thrivecart",
        operationKey: "/orders",
        type: "trigger",
        ai: { id: "thrivecart_new_order" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New ThriveCart Purchase",
            operationIcon: "./thrivecart.svg",
            operationIconToolbox: "./thrivecart.svg",
        },
        serviceId: "thrivecart",
        operationKey: "/purchase",
        type: "trigger",
        ai: { id: "thrivecart_new_purchase" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: true,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "New Supabase User",
            operationIcon: "./supabase.svg",
            operationIconToolbox: "./supabase.svg",
        },
        serviceId: "supabase",
        operationKey: "/user-created",
        type: "trigger",
        ai: { id: "supabase_new_user" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "Supabase User Updated",
            operationIcon: "./supabase.svg",
            operationIconToolbox: "./supabase.svg",
        },
        serviceId: "supabase",
        operationKey: "/user-updated",
        type: "trigger",
        ai: { id: "supabase_user_updated" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
    {
        display: {
            label: "Supabase User Deleted",
            operationIcon: "./supabase.svg",
            operationIconToolbox: "./supabase.svg",
        },
        serviceId: "supabase",
        operationKey: "/user-deleted",
        type: "trigger",
        ai: { id: "supabase_user_deleted" },
        inputFields: {},
        outputFields: {},
        mustConfig: {
            configStep: false,
            mapOutputFields: false,
        },
    },
];
/** What a step is found by: its service and its operation key together. The list holds one key twice (New WooCommerce Customer). */
const toolboxKey = (step) => `${step.serviceId}${step.operationKey}`;
exports.toolboxKey = toolboxKey;
/** Whether the toolbox offers the step: it is not hidden and it is not a note. Only these are built, or marked not for the AI. */
const isToolboxStep = (step) => !step.isHidden && step.type !== "decoration";
exports.isToolboxStep = isToolboxStep;
//# sourceMappingURL=toolbox_steps.js.map